/**
 * MigrationMap — proves nothing was dropped on the floor when the
 * old Editorial-desk was rebuilt into the OS Admin shell.
 *
 * Per founder ruling 14 Jun 2026 (Stop Rule, Phase 1a):
 *   "Please map every current admin tab into the new Command Center.
 *    Nothing should disappear."
 *
 * Reads /api/os-admin/migration-map (the single source of truth) and
 * renders the legacy-tab → new-route table.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

export default function MigrationMap() {
  const [map, setMap] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/os-admin/migration-map");
        setMap(data);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  const grouped = (map?.entries || []).reduce((acc, e) => {
    (acc[e.new_group] = acc[e.new_group] || []).push(e);
    return acc;
  }, {});

  return (
    <div data-testid="os-migration-map">
      <div className="os-pagehead">
        <h1>Migration Map</h1>
        <span className="count">
          {map ? `${map.entries.length} routes · ${map.placeholders.length} placeholders` : "loading…"}
        </span>
      </div>
      <div className="os-pagesub">
        Every legacy Editorial-desk tab → its new home in the Concierge OS Admin shell.
        Phase-2 entries route to a placeholder page that explains what will live there.
      </div>

      {err && (
        <div
          style={{
            color: "#E5B6AE",
            border: "1px solid rgba(199,106,91,0.4)",
            padding: 12,
            borderRadius: 6,
            marginBottom: 12,
            fontSize: 12.5,
          }}
        >
          {err}
        </div>
      )}

      {Object.entries(grouped).map(([group, rows]) => (
        <div key={group} style={{ marginBottom: 28 }} data-testid={`os-mig-group-${group.toLowerCase().replace(/\s+|&/g, "-")}`}>
          <div className="os-section-h">{group}</div>
          <table className="os-mig-table">
            <thead>
              <tr>
                <th>Legacy tab</th>
                <th>New route</th>
                <th>Label</th>
                <th>Status</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} data-testid={`os-mig-row-${r.new_route}`}>
                  <td style={{ color: "var(--os-muted)" }}>{r.legacy_tab}</td>
                  <td className="os-ref">
                    <Link to={r.new_route} style={{ textDecoration: "underline" }}>
                      {r.new_route}
                    </Link>
                  </td>
                  <td>{r.new_label}</td>
                  <td>
                    <span className={`os-status-${r.status}`}>{r.status}</span>
                  </td>
                  <td style={{ color: "var(--os-faint)", fontSize: 11.5 }}>{r.note || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {map?.deferred_phases?.length > 0 && (
        <>
          <div className="os-section-h">Deferred · upcoming phases</div>
          <ul style={{ margin: "0 0 0 18px", fontSize: 12.5, color: "var(--os-muted)", lineHeight: 1.8 }}>
            {map.deferred_phases.map((d, i) => (
              <li key={i} data-testid={`os-deferred-${i}`}>{d}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
